import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, BarChart3, Sparkles, Users, X } from 'lucide-react'
import type { ListingAuditReport } from '@/lib/api/listing-builder.types'
import { SectionCard } from '@/components/v2/tools/amazon-listing/shared/SectionCard'
import { RecommendationCards } from '@/components/v2/tools/amazon-listing/shared/RecommendationCards'
import { SalesPsychologyCard } from '@/components/v2/tools/amazon-listing/shared/SalesPsychologyCard'

interface BulletsAnalysisProps {
  analysis: ListingAuditReport['bullets_analysis']
}

const BULLET_OPTIMAL = 150
const BULLET_MAX = 200

const TRIGGER_LABELS: Record<string, string> = {
  trust: 'Vertrauen',
  fear: 'Dringlichkeit',
  aspiration: 'Verlangen wecken',
  social_proof: 'Sozialer Beweis',
  urgency: 'Zeitdruck',
  safety: 'Sicherheit',
  exclusivity: 'Exklusivität',
  curiosity: 'Neugier',
  scarcity: 'Knappheit',
  recommendation: 'Empfehlung',
  perfection: 'Perfektion',
  recognition: 'Anerkennung',
  belonging: 'Zugehörigkeit',
  comfort: 'Komfort',
  nostalgia: 'Nostalgie',
  authority: 'Autorität',
}

export function BulletsAnalysis({ analysis }: BulletsAnalysisProps) {
  const { t } = useTranslation()
  if (!analysis) return null
  const r = (key: string) => t(`amazonListing.report.${key}`)
  const mc = analysis.market_comparison

  return (
    <div className="space-y-4">
      <SectionCard title={r('bulletsAnalysis')} score={analysis.score}>
        {(analysis.analysis_incomplete || (!analysis.per_bullet?.length && analysis.score === 0)) && (
          <div className="flex items-center gap-2 p-3 mb-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {r('analysisIncomplete')}
          </div>
        )}
        {analysis.total_char_count != null && (
          <div className="mb-3 p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
            <div className="flex justify-between text-xs text-neutral-500 mb-1">
              <span>{analysis.total_char_count} / 1000 {r('totalCharacters')}</span>
              <span>{analysis.total_char_count > 1000 ? r('overLimit') : r('withinLimit')}</span>
            </div>
            <div className="h-1.5 bg-neutral-200 dark:bg-neutral-600 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${analysis.total_char_count <= 1000 ? 'bg-green-500' : 'bg-red-500'}`} style={{ width: `${Math.min((analysis.total_char_count / 1000) * 100, 100)}%` }} />
            </div>
          </div>
        )}
        <div className="space-y-3">
          {analysis.per_bullet?.map((b) => <BulletItem key={b.position} bullet={b} r={r} />)}
        </div>
      </SectionCard>

      {mc && (mc.source_count > 0 || mc.competitor_avg_count > 0) && (
        <MarketComparison mc={mc} r={r} />
      )}

      {analysis.sales_psychology && <SalesPsychologyCard data={analysis.sales_psychology} />}
      {analysis.missing_usps?.length > 0 && <MissingUsps usps={analysis.missing_usps} r={r} />}
      {analysis.recommendations?.length > 0 && <RecommendationCards recs={analysis.recommendations} t={t} />}
    </div>
  )
}

function MarketComparison({ mc, r }: {
  mc: ListingAuditReport['bullets_analysis']['market_comparison']; r: (k: string) => string
}) {
  const { t } = useTranslation()
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
      <h4 className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
        <BarChart3 className="w-4 h-4 text-neutral-500" /> {r('bulletMarketComparison')}
      </h4>
      <div className="grid grid-cols-2 gap-3">
        <CompStat label={r('bulletCount')} source={mc.source_count} competitor={mc.competitor_avg_count} vs={t('amazonListing.report.vs')} />
        <CompStat label={r('bulletAvgLength')} source={mc.source_avg_length} competitor={mc.competitor_avg_length} vs={t('amazonListing.report.vs')} />
      </div>
    </div>
  )
}

function CompStat({ label, source, competitor, vs }: { label: string; source: number; competitor: number; vs: string }) {
  return (
    <div className="rounded-lg bg-neutral-50 dark:bg-neutral-700/50 p-3 text-center">
      <div className="text-xs text-neutral-500 mb-1">{label}</div>
      <div className="text-sm">
        <span className="font-bold text-orange-600">{Math.round(source)}</span>
        <span className="text-neutral-400 mx-1">{vs}</span>
        <span className="font-medium text-neutral-600 dark:text-neutral-300">ø {Math.round(competitor)}</span>
      </div>
    </div>
  )
}

function bulletLengthColor(len: number): string {
  if (len <= BULLET_OPTIMAL) return 'bg-green-500'
  if (len <= BULLET_MAX) return 'bg-yellow-500'
  return 'bg-red-500'
}

function bulletGrade(score: number): { letter: string; color: string; textColor: string; bgColor: string } {
  if (score >= 85) return { letter: 'A', color: 'bg-green-700', textColor: 'text-green-700 dark:text-green-400', bgColor: 'bg-green-500' }
  if (score >= 70) return { letter: 'B', color: 'bg-green-500', textColor: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-500' }
  if (score >= 55) return { letter: 'C', color: 'bg-yellow-500', textColor: 'text-yellow-600 dark:text-yellow-400', bgColor: 'bg-yellow-500' }
  if (score >= 40) return { letter: 'D', color: 'bg-orange-500', textColor: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-500' }
  if (score >= 20) return { letter: 'E', color: 'bg-red-500', textColor: 'text-red-500 dark:text-red-400', bgColor: 'bg-red-500' }
  return { letter: 'F', color: 'bg-red-800', textColor: 'text-red-700 dark:text-red-400', bgColor: 'bg-red-800' }
}

function BulletItem({ bullet, r }: { bullet: ListingAuditReport['bullets_analysis']['per_bullet'][0]; r: (k: string) => string }) {
  const charCount = bullet.text_preview?.length ?? 0
  const pct = Math.min((charCount / BULLET_MAX) * 100, 100)
  const normalizedScore = Math.min(bullet.score, 100)
  const grade = bulletGrade(normalizedScore)

  return (
    <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-4">
      <div className="flex flex-wrap items-center justify-between gap-1 mb-2">
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          <span className="text-sm font-bold text-neutral-900 dark:text-white">#{bullet.position}</span>
          {bullet.is_benefit_first && (
            <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">{r('benefitFirst')}</span>
          )}
          {bullet.emotional_trigger && (
            <span className="px-1.5 py-0.5 rounded text-2xs bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400" title={bullet.emotional_trigger}>
              {bullet.emotional_trigger_label || TRIGGER_LABELS[bullet.emotional_trigger] || bullet.emotional_trigger}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="w-12 h-2 bg-neutral-200 dark:bg-neutral-600 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${grade.bgColor}`} style={{ width: `${normalizedScore}%` }} />
          </div>
          <span className={`text-sm font-bold ${grade.textColor}`}>{normalizedScore}</span>
        </div>
      </div>

      {bullet.text_preview && (
        <p className="text-sm text-neutral-700 dark:text-neutral-300 mb-2 italic">{bullet.text_preview}</p>
      )}

      <div className="mb-2">
        <div className="flex justify-between text-xs text-neutral-500 mb-0.5">
          <span>{charCount} {r('characters')}</span>
          <span>{BULLET_OPTIMAL} {r('bulletOptimal')}</span>
        </div>
        <div className="h-1.5 bg-neutral-200 dark:bg-neutral-600 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${bulletLengthColor(charCount)}`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      {bullet.analysis && <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">{bullet.analysis}</p>}

      {(bullet.format_issues?.length ?? 0) > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {bullet.format_issues!.map((issue: string) => (
            <span key={issue} className="px-1.5 py-0.5 rounded text-2xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
              {r(`formatIssue_${issue}`) || issue}
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-1">
        {bullet.keywords_found?.map((kw) => (
          <span key={kw} className="px-1.5 py-0.5 rounded text-xs bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400">{kw}</span>
        ))}
        {bullet.keywords_missing?.map((kw) => (
          <span key={kw} className="px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 line-through">{kw}</span>
        ))}
      </div>

      {bullet.recommendation && (
        <p className="text-sm text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 rounded-md px-3 py-2 mt-2">{bullet.recommendation}</p>
      )}
    </div>
  )
}

type UspItem = ListingAuditReport['bullets_analysis']['missing_usps'][0]

function MissingUsps({ usps, r }: { usps: UspItem[]; r: (k: string) => string }) {
  const [dismissed, setDismissed] = useState<Set<number>>(new Set())
  const visible = usps.filter((_, i) => !dismissed.has(i))
  if (visible.length === 0) return null

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
      <h4 className="text-sm font-medium mb-3">{r('missingUsps')}</h4>
      <div className="space-y-2">
        {usps.map((usp, i) => {
          if (dismissed.has(i)) return null
          const isAi = usp.source === 'ai_suggested'
          return (
            <div key={i} className={`text-sm p-3 rounded-lg border ${isAi ? 'bg-primary-50 dark:bg-primary-900/10 border-primary-200 dark:border-primary-800' : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  {isAi ? <Sparkles className="w-3.5 h-3.5 text-primary-500 flex-shrink-0" /> : <Users className="w-3.5 h-3.5 text-yellow-600 flex-shrink-0" />}
                  <span className={`font-medium ${isAi ? 'text-primary-800 dark:text-primary-300' : 'text-yellow-800 dark:text-yellow-300'}`}>{usp.usp}</span>
                  <span className={`px-1.5 py-0.5 rounded text-2xs font-medium ${isAi ? 'bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400' : 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>
                    {isAi ? r('uspAiSuggested') : r('uspFromCompetitor')}
                  </span>
                </div>
                {isAi && (
                  <button onClick={() => setDismissed((s) => new Set(s).add(i))} className="text-neutral-400 hover:text-neutral-600 flex-shrink-0" title={r('uspDismiss')}>
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {!isAi && usp.competitor_source && <span className="text-2xs text-neutral-400 font-mono mt-1">{usp.competitor_source}</span>}
            </div>
          )
        })}
      </div>
    </div>
  )
}
